import { useRouter } from 'next/router';
import ArrowLeft from '~/icons/iconography/arrow/left.svg';
import Download from '~/icons/iconography/download.svg';
import { ShopTypes } from '~/lib/data-contract';
import { useTranslations } from '~/services/umbraco/hooks';
import { LinkButton } from '~/shared/components/Button';
import { ImageOverlay, ProductImage } from '~/shared/components/Image';
import { Link } from '~/shared/components/Link';
import PlaceholderImage from '~/shared/components/NewComponents/PlaceholderImage';
import { Text } from '~/shared/components/Text';
import { useCurrency } from '~/shared/hooks';
import { theme } from '~/theme';
import getInternalInvoiceUrl from '../../../M202AccountInformation/helper/getInvoiceUrl';
import OrderDetailsCardBottom from '../CardBottom';
import OrderDetailsInformation from '../Information';
import {
    BackButton,
    Card,
    CardContainer,
    EmptyList,
    Header,
    Info,
    Price,
    Quantity,
    QuantityWrapper,
    Section,
    StyledHeaderText,
    StyledPrice,
    StyledText,
    Thumbnail,
} from './styles';

type Props = {
    data: ShopTypes.OrderDetailsResponse;
};

export default function OrderDetails({ data }: Props) {
    const { translate } = useTranslations();
    const formatCurrency = useCurrency();
    const router = useRouter();

    const url = getInternalInvoiceUrl(data?.customerNumber, data?.orderNumber, null);

    return (
        <>
            <BackButton onClick={router.back} aria-label={translate('mit-fog.orderDetails.goBack')}>
                <ArrowLeft />
                <Text variant="display6">{translate('mit-fog.orderDetails.goBack')}</Text>
            </BackButton>

            <Section>
                <Header>
                    <StyledHeaderText as="h2" variant="display2">
                        {translate('mit-fog.orderDetails.title')} {data.orderNumber}
                    </StyledHeaderText>

                    {data.hasInvoice ? (
                        <LinkButton
                            href={url}
                            color={theme.colors.brandBlue}
                            variant="fill"
                            disabled={!Boolean(data.customerNumber || data.orderNumber)}
                            customIcon={<Download />}
                            aria-label={translate('mit-fog.orderDetails.download')}
                        >
                            {translate('mit-fog.orderDetails.download')}
                        </LinkButton>
                    ) : null}
                </Header>

                <OrderDetailsInformation data={data} />

                <Text as="h3" variant="display5">
                    {translate('mit-fog.orderDetails.subtitle')} ({data?.lines?.length}{' '}
                    {translate('mit-fog.orderDetails.orders')})
                </Text>
                <CardContainer>
                    {data?.lines ? (
                        <>
                            {data.lines.map((line, index) => (
                                <Card key={`${line.productId}-${index}`}>
                                    <Thumbnail>
                                        {line.imageUrl ? (
                                            <ProductImage
                                                src={line.imageUrl ?? '-'}
                                                width={76}
                                                height={76}
                                                alt={line.name ?? '-'}
                                                style={{ objectFit: 'contain' }}
                                            />
                                        ) : (
                                            <PlaceholderImage />
                                        )}
                                        <ImageOverlay />
                                    </Thumbnail>

                                    <Info>
                                        {line.brand ? (
                                            <Text variant="uiSplashes">{line.brand}</Text>
                                        ) : null}

                                        {line?.itemStatus && line?.itemCanonicalUrl ? (
                                            line.name || line.name2 ? (
                                                <Link
                                                    href={line.itemCanonicalUrl}
                                                    animation="reverse"
                                                    target="_blank"
                                                >
                                                    {line.name} {line.name2}
                                                </Link>
                                            ) : (
                                                <Link
                                                    href={line.itemCanonicalUrl}
                                                    animation="reverse"
                                                    target="_blank"
                                                >
                                                    {line.variantName}
                                                </Link>
                                            )
                                        ) : (
                                            <>
                                                {line.name || line.name2 ? (
                                                    <Text variant="display5">
                                                        {line?.name} {line.name2}
                                                    </Text>
                                                ) : (
                                                    <Text variant="display5">
                                                        {line.variantName}
                                                    </Text>
                                                )}
                                            </>
                                        )}

                                        {line.variantType && line.variantName ? (
                                            <Text variant="uiLinks">
                                                {line.variantType.charAt(0).toUpperCase() +
                                                    line.variantType.slice(1)}
                                                : {line.variantName}
                                            </Text>
                                        ) : null}

                                        <StyledText variant="uiLinks">
                                            {line.productId
                                                ? `${translate('generic.itemNumber')} ${
                                                      line.productId
                                                  }`
                                                : '-'}
                                        </StyledText>
                                    </Info>

                                    <Quantity>
                                        <div />
                                        <div />
                                        <div />
                                        <QuantityWrapper>
                                            <Text variant="display6">
                                                {line.quantity && line.unitPrice
                                                    ? `${line.quantity} x ${formatCurrency(
                                                          line.unitPrice,
                                                      )}`
                                                    : '-'}
                                            </Text>

                                            <StyledText variant="display6">
                                                {line.unitOfMeasure
                                                    ? `pr. ${line.unitOfMeasure?.toLocaleLowerCase()}.`
                                                    : '-'}
                                            </StyledText>
                                        </QuantityWrapper>
                                    </Quantity>

                                    <Price>
                                        <div />
                                        <div />
                                        <div />
                                        <StyledPrice>
                                            <Text variant="display6">
                                                {formatCurrency(line.linePrice)}
                                            </Text>
                                        </StyledPrice>
                                    </Price>
                                </Card>
                            ))}
                        </>
                    ) : (
                        <EmptyList>
                            <Text>{translate('mit-fog.orderDetails.emptyList')}</Text>
                        </EmptyList>
                    )}
                </CardContainer>
                <OrderDetailsCardBottom data={data} />
            </Section>
        </>
    );
}
